import {PayPalButtons, PayPalScriptProvider} from "@paypal/react-paypal-js"
import {useMutation} from "@tanstack/react-query"
import {Spin, Typography} from "antd"
import moment from "moment"
import {BaseButton} from "components"
import {handleError, toastSuccess} from "helpers"
import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {studentPaymentService} from "services"
import {
    CreateRecurringPaymentPayload,
    PreparePaypalRecurringSubscriptionApiResponse,
    RecurringPaymentInterval,
    StudentPaymentSupportedCurrency
} from "types/student-account/student-payment"

type ExistingPaypalPrepare = {
    recurringPaymentId: number
    paypalPlanId: string
}

type Props = {
    profileId: number
    clientId: string
    amountInCents: number
    currency: StudentPaymentSupportedCurrency
    interval: RecurringPaymentInterval
    intervalCount?: number
    cancelAt: moment.Moment | null
    paymentPlanItems?: CreateRecurringPaymentPayload["paymentPlanItems"]
    paymentPlanId?: number | null
    existingPrepare?: ExistingPaypalPrepare
    onSuccess: () => void
}

export const PaypalRecurringSubscriptionForm = ({
    profileId,
    clientId,
    amountInCents,
    currency,
    interval,
    intervalCount = 1,
    cancelAt,
    paymentPlanItems,
    paymentPlanId,
    existingPrepare,
    onSuccess
}: Props) => {
    const {t} = useTranslation(["payment", "common"])
    const [prepareResult, setPrepareResult] = useState<PreparePaypalRecurringSubscriptionApiResponse | null>(
        existingPrepare ?? null
    )

    const cancelAtKey = cancelAt?.toISOString() ?? null
    const existingPrepareKey = existingPrepare
        ? `${existingPrepare.recurringPaymentId}-${existingPrepare.paypalPlanId}`
        : null

    useEffect(() => {
        setPrepareResult(existingPrepare ?? null)
    }, [existingPrepareKey, existingPrepare])

    const prepareMutation = useMutation({
        mutationFn: () =>
            studentPaymentService.preparePaypalRecurringSubscription({
                profileId,
                amountInCents,
                currency,
                interval,
                intervalCount,
                cancelAt: cancelAtKey,
                paymentPlanItems,
                ...(paymentPlanId ? {paymentPlanId} : {})
            }),
        onSuccess: (data) => {
            if (data?.paypalPlanId && data?.recurringPaymentId) {
                setPrepareResult(data)
            }
        },
        onError: (error) => handleError(error)
    })

    const activateMutation = useMutation({
        mutationFn: ({subscriptionId, recurringPaymentId}: {subscriptionId: string; recurringPaymentId: number}) =>
            studentPaymentService.activatePaypalRecurringSubscription({
                recurringPaymentId,
                subscriptionId
            }),
        onError: (error) => handleError(error),
        onSuccess: () => {
            toastSuccess(t("msg.recurringPayment.paypalAddedSuccessfully"))
            onSuccess()
        }
    })

    if (amountInCents <= 0) {
        return (
            <Typography.Paragraph type="secondary">{t("msg.recurringPayment.paypalEnterAmount")}</Typography.Paragraph>
        )
    }

    if (!prepareResult?.paypalPlanId || !prepareResult.recurringPaymentId) {
        if (prepareMutation.isLoading) {
            return <Spin />
        }
        return (
            <BaseButton
                size="small"
                title={t("common:action.add")}
                className="w-full mt-24"
                loading={prepareMutation.isLoading}
                onClick={() => prepareMutation.mutate()}
            />
        )
    }

    const {paypalPlanId, recurringPaymentId} = prepareResult

    if (activateMutation.isLoading) {
        return <Spin />
    }

    return (
        <PayPalScriptProvider
            key={`${recurringPaymentId}-${paypalPlanId}`}
            options={{
                clientId,
                intent: "subscription",
                vault: true,
                currency: currency.toUpperCase()
            }}>
            <PayPalButtons
                style={{
                    shape: "rect",
                    color: "blue",
                    layout: "vertical",
                    label: "subscribe"
                }}
                createSubscription={(_, actions) =>
                    actions.subscription.create({
                        plan_id: paypalPlanId,
                        // Pass our internal IDs so they appear on the subscription resource
                        // in PayPal webhooks, enabling correlation without a DB lookup.
                        custom_id: [`rp:${recurringPaymentId}`, paymentPlanId ? `pp:${paymentPlanId}` : null]
                            .filter(Boolean)
                            .join(",")
                            .slice(0, 127)
                    })
                }
                onApprove={async (data) => {
                    const subscriptionId = data.subscriptionID
                    if (!subscriptionId) {
                        handleError(new Error(t("msg.recurringPayment.paypalMissingSubscriptionId")))
                        return
                    }
                    await activateMutation.mutateAsync({
                        subscriptionId,
                        recurringPaymentId
                    })
                }}
                onError={() => {
                    handleError(new Error(t("msg.recurringPayment.paypalButtonError")))
                }}
            />
        </PayPalScriptProvider>
    )
}
